import React from "react";
import { Link } from "react-router-dom";

type Props = {
  eyebrow?: string;
  title: React.ReactNode;
  description: string;
  ctaLabel?: string;
  ctaTo?: string;
  /** caminho em /public ou URL externa - pode ser video mp4/webm ou imagem */
  mediaSrc: string;
  mediaType?: "video" | "image" | "auto";
  poster?: string;
  className?: string;
};

export default function DiagonalHeroVideo({
  eyebrow = "•  PDV MÓVEL  ·  SMARTPOS",
  title,
  description,
  ctaLabel = "Ver o PDV por completo",
  ctaTo = "/lukos-pdv",
  mediaSrc,
  mediaType = "auto",
  poster,
  className = "",
}: Props) {
  const isVideo =
    mediaType === "video" ||
    (mediaType === "auto" && /\.(mp4|webm|mov)(\?|$)/i.test(mediaSrc));

  return (
    <section
      className={`relative w-full overflow-hidden bg-[#0b0520] ${className}`}
      aria-label="Hero PDV móvel"
    >
      {/* linha diagonal sutil como no print */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: `linear-gradient(180deg, rgba(124,244,125,0.0) 0%, rgba(255,255,255,0.06) 100%)`,
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 hidden lg:block"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b0520] via-[#0b0520] to-transparent" />
        {/* linha fina diagonal */}
        <div
          className="absolute left-0 right-0 h-px bg-white/10"
          style={{
            top: "52%",
            transform: "rotate(-12deg)",
            transformOrigin: "left center",
          }}
        />
      </div>

      <div className="relative mx-auto max-w-[1920px]">
        <div className="grid grid-cols-1 lg:grid-cols-[0.78fr_1.22fr] min-h-[420px] lg:min-h-[460px]">
          {/* ESQUERDA - TEXTO */}
          <div className="relative z-10 flex items-center px-6 sm:px-10 lg:px-16 xl:px-24 2xl:px-[120px] py-12 lg:py-16">
            <div className="max-w-[560px]">
              <p className="text-[10px] tracking-[0.28em] font-bold text-[#7cf47d] uppercase mb-5">
                {eyebrow}
              </p>

              <h2 className="text-white text-[30px] sm:text-[38px] lg:text-[42px] font-extrabold leading-[0.95] tracking-tight">
                {title}
              </h2>

              <p className="mt-5 text-[13.5px] sm:text-[14px] leading-[1.65] text-white/70 max-w-[420px]">
                {description}
              </p>

              <Link
                to={ctaTo}
                className="mt-8 inline-flex items-center gap-2 text-[13px] font-bold text-white hover:text-white/80 transition-colors group"
              >
                {ctaLabel}
                <span
                  aria-hidden
                  className="transition-transform group-hover:translate-x-1"
                >
                  →
                </span>
              </Link>
            </div>
          </div>

          {/* DIREITA - MÍDIA COM CORTE DIAGONAL - vídeo bem mais visível */}
          <div className="relative h-[320px] sm:h-[420px] lg:h-auto lg:min-h-[460px] overflow-hidden">
            <div
              className="absolute inset-0"
              style={{
                clipPath: "polygon(9% 0, 100% 0, 100% 100%, 0 100%)",
              }}
            >
              {isVideo ? (
                <video
                  src={mediaSrc}
                  poster={poster}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  className="h-full w-full object-cover object-[center_30%]"
                />
              ) : (
                <img
                  src={mediaSrc}
                  alt=""
                  className="h-full w-full object-cover object-[center_30%]"
                  loading="eager"
                />
              )}

              {/* overlay gradiente suave - não bloqueia o vídeo */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#0b0520]/55 via-[#0b0520]/18 to-transparent" />
              <div className="absolute inset-0 bg-[#0b0520]/5" />
              {/* logo Lukos no canto como no print */}
              <img
                src="/logo.png"
                alt="Lukos"
                className="absolute top-4 right-4 w-12 sm:w-16 h-auto object-contain opacity-90 drop-shadow-lg hidden sm:block"
                loading="lazy"
              />
            </div>

            <div
              aria-hidden
              className="absolute inset-0 hidden lg:block pointer-events-none"
              style={{
                clipPath: "polygon(9% 0, 100% 0, 100% 100%, 0 100%)",
                borderLeft: "1px solid rgba(255,255,255,0.16)",
                transform: "translateX(-0.5px)",
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
